import { store } from "../store/store";
import { Auth, authorizeActions } from "../store/authorize";
import { authStore } from "./authStore";
import { WindowStorage } from "../services/storage";
import { StorageType } from "../services/storage.default";

/**
 * Single-writer for auth: every change goes through here and updates the three places in lock-step —
 * the module authStore (read by interceptors/SignalR), Redux (read by React render/permission selectors),
 * and browser storage (session vs local, chosen via "keep me logged in"). This is what keeps them from drifting.
 */

// Dedicated storage instances (the auth blob + the persisted storage-type flag, which always lives in localStorage).
const flagStorage = new WindowStorage(undefined, "local");
const authStorage = new WindowStorage();

function currentStorageType(): StorageType {
  return flagStorage.get<StorageType>("AUTH_STORAGE_TYPE", "session") ?? "session";
}

function dispatchAuth(auth: Auth | null): void {
  authStore.set(auth);
  store.dispatch(auth ? authorizeActions.signIn(auth) : authorizeActions.signOut());
}

/**
 * Persist + broadcast a new auth. Pass keepLoggedIn on LOGIN to pick local (persistent) vs session storage; omit it
 * on REFRESH to reuse the storage already chosen.
 */
export function applyAuth(auth: Auth, keepLoggedIn?: boolean): void {
  const type: StorageType = keepLoggedIn === undefined
    ? currentStorageType()
    : keepLoggedIn ? "local" : "session";

  authStorage.on(type);
  if (keepLoggedIn !== undefined) flagStorage.set("AUTH_STORAGE_TYPE", type);

  authStorage.set("AUTH", auth);
  dispatchAuth(auth);
}

/** Clear auth everywhere (storage + module + Redux). The route guard reacts to the Redux change and redirects. */
export function clearAuth(): void {
  authStorage.clear(true); // clears both session + local
  dispatchAuth(null);
}

/** Read persisted auth on app start and hydrate the module + Redux WITHOUT re-writing storage. */
export function bootstrapAuth(): void {
  dispatchAuth(readStoredAuth());
}

/** The currently-persisted auth (the cross-tab source of truth — another tab's refresh lands here). */
export function readStoredAuth(): Auth | null {
  authStorage.on(currentStorageType());
  return authStorage.get<Auth>("AUTH") ?? null;
}

/**
 * Adopt whatever is in storage if it differs from our in-memory copy — used to pick up a token another tab just
 * refreshed (rotating refresh tokens reject reuse, so a stale in-memory token would 400). Returns the live auth.
 */
export function syncFromStorage(): Auth | null {
  const stored = readStoredAuth();
  if ((stored?.token ?? null) !== (authStore.get()?.token ?? null)) {
    dispatchAuth(stored ?? null);
  }
  return authStore.get();
}

// Cross-tab sync: localStorage changes fire a 'storage' event in OTHER tabs. When another tab refreshes (AUTH key)
// or logs out (clear → null key), adopt the change here so every tab shares the latest token. (sessionStorage is
// per-tab and never conflicts, so this only matters for "keep me logged in"/localStorage.)
if (typeof window !== "undefined") {
  window.addEventListener("storage", (e) => {
    if (e.key === null || e.key === "AUTH") syncFromStorage();
  });
}
